import { useCallback, useEffect, useRef, type ReactNode } from 'react'
import { z } from 'zod'

import { RecordReachProvider, type RecordReachLookup } from '@/shared/access'
import { parseCursorPage } from '@/shared/api/paging'
import { useApiClient } from '@/shared/api/use-api-client'

import { classRoute } from './class-form/class'

/** Which of up to 100 classes the caller may open, as Class Details answers. */
export const classReachApi = '/classes/v1/class-reach'

const reachedSchema = z.object({ id: z.string() })

/**
 * Links a class's name only for a caller who may open it (112242): Class Reach, asked for the
 * linked ids, answers with those in their classes read reach, an editing delegate's classes among
 * them, so no class link leads to a page that is not found.
 */
export function ClassReachProvider(props: { readonly children: ReactNode }) {
  const client = useApiClient()
  // Kept current without changing the lookup, which would ask again: the client changes when sign-in settles.
  const api = useRef(client)
  useEffect(() => {
    api.current = client
  }, [client])

  const lookup = useCallback<RecordReachLookup>(async (ids) => {
    const query = new URLSearchParams({ ids: ids.join(',') })
    const found = parseCursorPage(await api.current.get(`${classReachApi}?${query.toString()}`), (item) => reachedSchema.parse(item))
    return new Set(found.data.map((reached) => reached.id))
  }, [])

  return (
    <RecordReachProvider route={classRoute} lookup={lookup}>
      {props.children}
    </RecordReachProvider>
  )
}
